import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { fehlerText, saveMausSpuren } from "@/lib/api";

/**
 * 🆕 05.09. (Alexanders Mess-Werkzeug) · Spieler-Verfolgung per Maus:
 * Linke Maustaste auf dem Spieler HALTEN und ihm nachfahren — solange
 * gehalten wird, läuft das Video (im gewählten Tempo) und alle 100 ms
 * wird (Videozeit, Bildposition) aufgezeichnet. Loslassen pausiert und
 * schließt die Spur ab. Die Spuren gehen als Gold-Referenz ans Backend
 * (data/maus_spuren/); scripts/maus_vergleich_v1.py misst daran, wie
 * gut das automatische Tracking funktioniert (Abstand, ID-Wechsel).
 *
 * Koordinaten werden auf das sichtbare Video-BILD normiert (0–1) —
 * das <video> nutzt object-fit: contain, Letterbox-Balken werden
 * herausgerechnet, damit die Punkte auflösungsunabhängig sind.
 */

/** Sichtbares Bild-Rechteck im <video>-Element (object-fit: contain). */
function bildRect(v) {
  const cw = v.clientWidth, ch = v.clientHeight;
  const vw = v.videoWidth || 16, vh = v.videoHeight || 9;
  const s = Math.min(cw / vw, ch / vh);
  const bw = vw * s, bh = vh * s;
  return { ox: (cw - bw) / 2, oy: (ch - bh) / 2, bw, bh };
}

const TEMPI = [0.25, 0.5, 1];

export default function SpielerVerfolger({ sequenceId, videoRef, aktiv, children }) {
  const canvasRef = useRef(null);
  const haltRef = useRef(false);
  const posRef = useRef(null);          // letzte Mausposition (normiert)
  const punkteRef = useRef([]);
  const tickRef = useRef(null);
  const [spuren, setSpuren] = useState([]);
  const [nummer, setNummer] = useState("");
  const [tempo, setTempo] = useState(0.5);
  const [nimmtAuf, setNimmtAuf] = useState(false);
  const [speichert, setSpeichert] = useState(false);

  const normPos = useCallback((e) => {
    const v = videoRef.current;
    if (!v) return null;
    const r = v.getBoundingClientRect();
    const b = bildRect(v);
    const x = (e.clientX - r.left - b.ox) / b.bw;
    const y = (e.clientY - r.top - b.oy) / b.bh;
    return [Math.min(1, Math.max(0, x)), Math.min(1, Math.max(0, y))];
  }, [videoRef]);

  const male = useCallback(() => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c) return;
    c.width = v.clientWidth;
    c.height = v.clientHeight;
    const b = bildRect(v);
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    const zeichne = (punkte, farbe, breit) => {
      if (punkte.length < 2) return;
      ctx.strokeStyle = farbe;
      ctx.lineWidth = breit;
      ctx.beginPath();
      punkte.forEach(([, x, y], i) => {
        const px = b.ox + x * b.bw, py = b.oy + y * b.bh;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      });
      ctx.stroke();
    };
    spuren.forEach((s) => zeichne(s.punkte, "rgba(74,222,128,0.35)", 2));
    zeichne(punkteRef.current, "#FFC83C", 3);
    if (posRef.current && haltRef.current) {
      const [x, y] = posRef.current;
      ctx.beginPath();
      ctx.arc(b.ox + x * b.bw, b.oy + y * b.bh, 7, 0, 7);
      ctx.strokeStyle = "#FFC83C";
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }, [spuren, videoRef]);

  useEffect(() => { if (aktiv) male(); }, [aktiv, male]);

  // Tempo live anwenden; beim Verlassen des Modus alles zurücksetzen.
  useEffect(() => {
    const v = videoRef.current;
    if (aktiv && v) v.playbackRate = tempo;
    return () => { if (v) v.playbackRate = 1; };
  }, [aktiv, tempo, videoRef]);
  useEffect(() => () => clearInterval(tickRef.current), []);

  const down = (e) => {
    if (e.button !== 0) return;
    const v = videoRef.current;
    const p = normPos(e);
    if (!v || !p) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* synthetische Events haben keine aktive pointerId */ }
    haltRef.current = true;
    posRef.current = p;
    punkteRef.current = [];
    setNimmtAuf(true);
    v.playbackRate = tempo;
    if (v.paused) v.play().catch(() => {});
    clearInterval(tickRef.current);
    // Ticker statt mousemove-Aufzeichnung: auch wenn Spieler UND Maus
    // stillstehen, läuft die Zeit — es braucht Punkte über die ZEIT.
    tickRef.current = setInterval(() => {
      const vv = videoRef.current;
      if (!vv || !haltRef.current || !posRef.current) return;
      const [x, y] = posRef.current;
      punkteRef.current.push([
        +vv.currentTime.toFixed(3), +x.toFixed(4), +y.toFixed(4),
      ]);
      male();
    }, 100);
  };

  const move = (e) => {
    if (!haltRef.current) return;
    const p = normPos(e);
    if (p) posRef.current = p;
  };

  const up = () => {
    if (!haltRef.current) return;
    haltRef.current = false;
    clearInterval(tickRef.current);
    setNimmtAuf(false);
    const v = videoRef.current;
    if (v && !v.paused) v.pause();
    const punkte = punkteRef.current;
    punkteRef.current = [];
    if (punkte.length < 5) {
      toast.warning("Spur zu kurz (unter 0,5 s) — verworfen");
      male();
      return;
    }
    setSpuren((prev) => [...prev, {
      label: `Spur ${prev.length + 1}`,
      nummer: nummer.trim() || null,
      punkte,
    }]);
  };

  const speichern = async () => {
    try {
      setSpeichert(true);
      const r = await saveMausSpuren(sequenceId, {
        spuren, geschwindigkeit: tempo,
      });
      toast.success(`${r.n_spuren} Spuren (${r.n_punkte} Punkte) gespeichert → ${r.datei}`);
      setSpuren([]);
    } catch (err) {
      toast.error(fehlerText(err) || "Speichern fehlgeschlagen");
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <>
      <div className="relative">
        {children}
        {aktiv && (
          <>
            <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />
            <div
              data-testid="verfolger-overlay"
              className="absolute inset-0 cursor-crosshair"
              style={{ touchAction: "none" }}
              onPointerDown={down}
              onPointerMove={move}
              onPointerUp={up}
              onPointerCancel={up}
              title="Linke Maustaste auf dem Spieler halten und nachfahren — Loslassen beendet die Spur"
            />
          </>
        )}
      </div>
      {aktiv && (
        <div className="bg-card border-t border-border px-3 py-2 space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-widest text-primary">
              🎯 Spieler verfolgen
            </span>
            <span className="text-[11px] text-muted-foreground">
              Maustaste auf dem Spieler <b>halten</b> und nachfahren — Video
              läuft solange, Loslassen beendet die Spur.
            </span>
            {nimmtAuf && (
              <span className="font-mono text-[10px] text-yellow-400 animate-pulse">
                ● AUFNAHME
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Tempo
            </span>
            {TEMPI.map((t) => (
              <Button
                key={t} size="sm" variant={tempo === t ? "default" : "ghost"}
                onClick={() => setTempo(t)}
                className="h-7 px-2 font-mono text-[10px]"
              >
                {t}×
              </Button>
            ))}
            <div className="w-px h-5 bg-border mx-1" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Rückennummer (optional)
            </span>
            <Input
              value={nummer}
              onChange={(e) => setNummer(e.target.value)}
              placeholder="z. B. 9"
              className="h-7 w-20 font-mono text-xs"
            />
            <div className="ml-auto" />
            <Button
              size="sm"
              disabled={!spuren.length || speichert}
              onClick={speichern}
              data-testid="verfolger-speichern"
              className="h-7 px-3"
            >
              {speichert
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <Save className="w-3.5 h-3.5 mr-1.5" />}
              {spuren.length} Spur{spuren.length === 1 ? "" : "en"} speichern
            </Button>
          </div>
          {spuren.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              {spuren.map((s, i) => {
                const dauer = s.punkte.length
                  ? (s.punkte[s.punkte.length - 1][0] - s.punkte[0][0])
                  : 0;
                return (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 border border-border rounded-sm px-2 py-0.5 font-mono text-[10px]"
                  >
                    {s.label}{s.nummer ? ` · #${s.nummer}` : ""} ·{" "}
                    {s.punkte[0][0].toFixed(1)}–
                    {s.punkte[s.punkte.length - 1][0].toFixed(1)} s ·{" "}
                    {dauer.toFixed(1)} s
                    <button
                      onClick={() => setSpuren((prev) => prev.filter((_, j) => j !== i))}
                      className="text-muted-foreground hover:text-destructive"
                      title="Spur verwerfen"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  );
}
